import { useAutoneTranslation } from '@autone/translations';
import { fNumber } from '@autone/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '../Button';

import { getPageRange } from './DataTablePagination.helpers';

interface DataTablePaginationProps {
  totalRecords: number;

  // pagination props
  currentPageIndex: number;
  onGetNextPage: () => void;
  onGetPreviousPage: () => void;
  canGetNextPage?: boolean;
  canGetPreviousPage?: boolean;

  // page size (limit) props
  pageSizeOptions: readonly number[];
  currPageSize: number;
  onPageSizeChange: (pageSize: number) => void;
}

/**
 * This is the base component for all table paginations. Use for server-side
 * pagination where pagination is managed outside `useReactTable`.
 */
export const DataTablePagination = ({
  totalRecords,
  currentPageIndex,
  onGetNextPage,
  onGetPreviousPage,
  canGetNextPage = true,
  canGetPreviousPage = true,
  pageSizeOptions,
  currPageSize,
  onPageSizeChange,
}: DataTablePaginationProps) => {
  const { common } = useAutoneTranslation();

  const { lowerBound, upperBound } = getPageRange(
    currentPageIndex,
    currPageSize,
    totalRecords,
  );

  const pageRangeText = `${fNumber(lowerBound)} - ${fNumber(upperBound)}`;

  return (
    <div className="flex gap-4 items-center">
      <div className="flex gap-2 items-center">
        <span className="block lowercase first-letter:uppercase">
          {common('rows-per-page')}
        </span>
        <select
          value={currPageSize}
          onChange={(e) => {
            onPageSizeChange(parseInt(e.target.value, 10));
          }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <span>{`${pageRangeText} ${common('of')} ${fNumber(totalRecords)}`}</span>
      <div>
        <Button
          variant="ghost"
          color="secondary"
          size="sm"
          disabled={!canGetPreviousPage}
          onClick={onGetPreviousPage}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          color="secondary"
          size="sm"
          disabled={!canGetNextPage}
          onClick={onGetNextPage}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
